import React, { useState } from 'react';
import { useGoogleReCaptcha, GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { useContactUsMutation } from '@data/contactUs/useContactUsMutation';
import { Loader } from '../Loader/Loader';

// @ts-ignore TODO: fix this type
const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY || '';

interface ContactUsFormData {
  name: string;
  email: string;
  subject: string;
  message: string;
  recaptchaToken?: string;
}

interface ContactUsErrors {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
  general?: string;
  recaptcha?: string;
}

const SUBJECT_OPTIONS = [
  { value: '', label: 'Please select a subject' },
  { value: 'registrations', label: 'Registrations' },
  { value: 'fixtures', label: 'Fixtures' },
  { value: 'other', label: 'Other' },
  { value: 'sponsorship', label: 'Sponsorship' },
  { value: 'press/media enquiries', label: 'Press/Media Enquiries' },
];

export type ContactUsProps = any;

const ContactUsForm = () => {
  const [formData, setFormData] = useState<ContactUsFormData>({
    name: '',
    email: '',
    subject: '',
    message: '',
    recaptchaToken: '',
  });
  const [errors, setErrors] = useState<ContactUsErrors>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const { executeRecaptcha } = useGoogleReCaptcha();
  const mutation = useContactUsMutation({
    onSuccess: (data: { success: boolean; error?: Record<keyof ContactUsFormData, string> }) => {
      if (data.success) {
        setIsSubmitted(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
        setErrors({});
      } else if (data.error) {
        setErrors(data.error);
      }
    },
    onError: (error: unknown) => {
      console.error('Contact form submission error:', error);
      setErrors({
        general: 'An error occurred while sending your message. Please try again.',
      });
    },
  });

  const validateForm = (): boolean => {
    const newErrors: ContactUsErrors = {};
    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!emailRegex.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    // Subject validation
    if (!formData.subject) {
      newErrors.subject = 'Please select a subject';
    }
    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field when user starts typing
    if (errors[name as keyof ContactUsErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }
    if (!executeRecaptcha) {
      setErrors({
        general: 'reCAPTCHA is not available. Please try again later.',
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
      return;
    }

    try {
      // Execute reCAPTCHA v3 with action 'contact_form'
      const recaptchaToken = await executeRecaptcha('contact_form');

      mutation.mutate({
        name: formData.name.trim(),
        email: formData.email.trim(),
        subject: formData.subject,
        message: formData.message.trim(),
        recaptchaToken,
      });
    } catch (error) {
      console.error('reCAPTCHA error:', error);
      setErrors({
        general: 'reCAPTCHA verification failed. Please try again.',
      });
      setFormData({ name: '', email: '', subject: '', message: '' });
    }
  };

  return (
    <div className="contact-us">
      {isSubmitted ? (
        <div className="contact-us__success">
          <h3>Thank you for your message!</h3>
          <p>We've received your inquiry and will get back to you as soon as possible.</p>
          <button
            type="button"
            onClick={() => {
              setIsSubmitted(false);
              setFormData({ name: '', email: '', subject: '', message: '', recaptchaToken: '' });
              setErrors({});
            }}
            className="contact-us__submit"
            style={{ marginTop: '1rem' }}
          >
            Send Another Message
          </button>
        </div>
      ) : (
        <>
          <h1 className="contact-us__title">Contact Us</h1>
          <p className="contact-us__subtitle">
            Have a question or need assistance? We'd love to hear from you. Fill out the form below and we'll get back
            to you as soon as possible.
          </p>
          <form className="contact-us__form" onSubmit={handleSubmit}>
            {errors.general && (
              <div className="contact-us__error" style={{ textAlign: 'center', marginBottom: '1rem' }}>
                {errors.general}
              </div>
            )}
            <div>
              <label htmlFor="name" className="contact-us__label contact-us__label--required">
                Full Name
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="contact-us__input"
                placeholder="Enter your full name"
                disabled={mutation.isPending}
              />
              {errors.name && <div className="contact-us__error">{errors.name}</div>}
            </div>
            <div>
              <label htmlFor="email" className="contact-us__label contact-us__label--required" id="email">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className="contact-us__input"
                placeholder="Enter your email address"
                disabled={mutation.isPending}
              />
              {errors.email && <div className="contact-us__error">{errors.email}</div>}
            </div>
            <div>
              <label htmlFor="subject" className="contact-us__label contact-us__label--required">
                Subject
              </label>
              <select
                id="subject"
                name="subject"
                value={formData.subject}
                className="contact-us__select"
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, subject: e.target.value }));
                  if (errors.subject) setErrors((prev) => ({ ...prev, subject: undefined }));
                }}
              >
                {SUBJECT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
              {errors.subject && <div className="contact-us__error">{errors.subject}</div>}
            </div>
            <div>
              <label htmlFor="message" className="contact-us__label contact-us__label--required">
                Message
              </label>
              <textarea
                id="message"
                name="message"
                value={formData.message}
                className="contact-us__textarea"
                placeholder="Please provide details about your inquiry..."
                rows={5}
                disabled={mutation.isPending}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, message: e.target.value }));
                  if (errors.message) setErrors((prev) => ({ ...prev, message: undefined }));
                }}
              />
              {errors.message && <div className="contact-us__error">{errors.message}</div>}
            </div>
            <div className="contact-us__recaptcha-container" id="recaptcha" />
            <button
              type="submit"
              disabled={mutation.isPending}
              className={`contact-us__submit ${mutation.isPending ? 'contact-us__submit--loading' : ''}`}
            >
              {mutation.isPending ? 'Sending...' : 'Send Message'}
            </button>
          </form>
          {mutation.isPending && (
            <div className="contact-us__loading">
              <Loader />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const ContactUs = ({}: ContactUsProps) => {
  return (
    <GoogleReCaptchaProvider
      reCaptchaKey={RECAPTCHA_SITE_KEY || ''}
      container={{
        element: 'recaptcha',
        parameters: {
          badge: 'inline',
          theme: 'light',
        },
      }}
    >
      <ContactUsForm />
    </GoogleReCaptchaProvider>
  );
};
